import React from 'react';
import { Navigate, Link, useParams } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import { comparePages, getComparePage, PRICING_AS_OF } from './compareData';
import './PricingPage.css';
import './RateCard.css';

function buildSchema(page) {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: `${SITE_URL}/compare` },
        { '@type': 'ListItem', position: 3, name: page.shortTitle, item: `${SITE_URL}/compare/${page.slug}` },
      ],
    },
  ];
}

export function CompareHub() {
  return (
    <>
      <SEO
        title="Compare AI Receptionists: UBOTIKA vs Smith.ai, Ruby & Answering Services"
        description="Honest, sourced comparisons of UBOTIKA against Smith.ai, Ruby, and traditional answering services — pricing models, bilingual coverage, booking, and the fine print, with links to every source."
        path="/compare"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: comparePages.map((p, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: p.shortTitle,
              url: `${SITE_URL}/compare/${p.slug}`,
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'Compare', item: `${SITE_URL}/compare` },
            ],
          },
        ]}
      />
      <main id="main-content" className="pricing-page ratecard-page">
        <section className="pricing-hero">
          <h1>Compare us. Honestly.</h1>
          <p className="pricing-sub">
            Side-by-side comparisons with every number read from the competitor&apos;s own
            published pricing and linked at the bottom of each page. Where a competitor is
            genuinely stronger, the page says so.
          </p>
        </section>

        <section className="ratecard-section">
          <div className="ratecard-grid">
            {comparePages.map((p) => (
              <article key={p.slug} className="ratecard-card">
                <h3>{p.shortTitle}</h3>
                <p className="ratecard-setup">{p.metaDescription.split('.')[0]}.</p>
                <Link to={`/compare/${p.slug}`} className="cta-button" style={{ marginTop: 'auto' }}>
                  Read the comparison
                </Link>
              </article>
            ))}
          </div>
          <p className="ratecard-fine">
            Competitor pricing as of {PRICING_AS_OF}. Vendors change prices — each page links
            the live source so you can check the current number yourself.
          </p>
        </section>
        <Footer />
      </main>
    </>
  );
}

function ComparePage() {
  const { slug } = useParams();
  const page = getComparePage(slug);
  if (!page) return <Navigate to="/compare" replace />;

  const related = comparePages.filter((p) => p.slug !== slug);

  return (
    <>
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/compare/${page.slug}`}
        schema={buildSchema(page)}
      />
      <main id="main-content" className="pricing-page ratecard-page">
        <section className="pricing-hero">
          <h1>{page.title}</h1>
          <p className="pricing-sub">{page.intro}</p>
        </section>

        <section className="ratecard-section">
          <div className="ratecard-grid">
            <article className="ratecard-vertical">
              <h3>{page.theirSide.heading}</h3>
              <ul className="ratecard-list">
                {page.theirSide.points.map((pt) => (
                  <li key={pt}><FaCheck /><span>{pt}</span></li>
                ))}
              </ul>
            </article>
            <article className="ratecard-vertical">
              <h3>{page.ourSide.heading}</h3>
              <ul className="ratecard-list">
                {page.ourSide.points.map((pt) => (
                  <li key={pt}><FaCheck /><span>{pt}</span></li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className="ratecard-section">
          <h2>Side by side</h2>
          <div className="ratecard-table-wrap">
            <table className="ratecard-table">
              <thead>
                <tr>{page.table[0].map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {page.table.slice(1).map((row) => (
                  <tr key={row[0]}>
                    {row.map((cell, i) => (
                      <td key={i} className={i === 1 ? 'num' : undefined}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="ratecard-fine">
            Competitor figures read from the sources below on {PRICING_AS_OF}. Pricing
            changes — check the live pages for current numbers:{' '}
            {page.sources.map((s, i) => (
              <span key={s.url}>
                {i > 0 && ' · '}
                <a href={s.url} target="_blank" rel="noopener noreferrer nofollow">{s.label}</a>
              </span>
            ))}
          </p>
        </section>

        <section className="ratecard-section">
          <h2>Common questions</h2>
          <div className="pricing-faq-list">
            {page.faqs.map((f) => (
              <article className="pricing-faq-item" key={f.q}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-pilot">
          <h2>The two-minute version</h2>
          <p>
            Call <a href="tel:+14244344541">(424) 434-4541</a> — an AI answers, because the
            demo is the product. Then get a free audit of the calls you&apos;re missing today,
            priced against your actual volume.
          </p>
          <Link to="/free-missed-lead-audit" className="cta-button">Get a free audit</Link>
        </section>

        <section className="ratecard-section">
          <p className="ratecard-fine">
            More comparisons:{' '}
            {related.map((p, i) => (
              <span key={p.slug}>
                {i > 0 && ' · '}
                <Link to={`/compare/${p.slug}`}>{p.shortTitle}</Link>
              </span>
            ))}
            {' '}· <Link to="/pricing">UBOTIKA pricing</Link>
          </p>
        </section>
        <Footer />
      </main>
    </>
  );
}

export default ComparePage;
